import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, ExternalLink, Calendar, Hash, Bell, Mail, Phone } from "lucide-react";
import { fromLocalInput } from "./codes-utils";

export default function CreateCodeModal({ files, onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [fileId, setFileId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/codes", {
        code: code.trim() || null,
        label: label.trim(),
        file_id: fileId || null,
        expires_at: fromLocalInput(expiresAt),
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        notify_email: notifyEmail.trim() || null,
        owner_email: ownerEmail.trim() || null,
        owner_phone: ownerPhone.trim() || null,
      });
      toast.success("Code created");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="create-modal">
      <form onSubmit={submit} className="glass rounded-2xl p-8 max-w-md w-full space-y-5 fade-up">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">New Access Code</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2">Code</label>
          <input
            data-testid="create-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Leave blank to auto-generate"
            className="w-full px-3 py-2.5 rounded font-mono-vault tracking-widest outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          />
        </div>

        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2">Label (optional)</label>
          <input
            data-testid="create-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Château Margaux 2015 — Lot #12"
            className="w-full px-3 py-2.5 rounded outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          />
        </div>

        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2">Linked File</label>
          <select
            data-testid="create-file-select"
            value={fileId}
            onChange={(e) => setFileId(e.target.value)}
            className="w-full px-3 py-2.5 rounded outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          >
            <option value="">— Assign later —</option>
            {files.map((f) => (
              <option key={f.id} value={f.id}>{f.original_filename}</option>
            ))}
          </select>
          {files.length === 0 && (
            <a href="/admin/files" className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--vault-gold)]">
              Upload your first file <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Expires
            </label>
            <input
              type="datetime-local"
              data-testid="create-expires-input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2.5 rounded outline-none text-sm"
              style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)", colorScheme: "dark" }}
            />
            <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Leave empty for never</p>
          </div>
          <div>
            <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
              <Hash className="w-3 h-3" /> Max uses
            </label>
            <input
              type="number"
              min="0"
              data-testid="create-max-uses-input"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              className="w-full px-3 py-2.5 rounded outline-none text-sm"
              style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
            />
            <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">0 = unlimited</p>
          </div>
        </div>

        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
            <Bell className="w-3 h-3" /> Notify email (optional)
          </label>
          <input
            type="email"
            data-testid="create-notify-input"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="you@verifinewines.com"
            className="w-full px-3 py-2.5 rounded outline-none text-sm"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          />
          <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Receive an email whenever this code is used.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Owner email</label>
            <input type="email" data-testid="create-owner-email-input" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@example.com" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} />
          </div>
          <div>
            <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Owner phone</label>
            <input type="tel" data-testid="create-owner-phone-input" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} placeholder="+351 912 345 678" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} />
          </div>
          <p className="col-span-2 text-[10px] text-[var(--vault-text-faint)] -mt-1">Access codes are sent to the owner email. Only admins can change it.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="create-submit" className="btn-gold px-5 py-2 rounded-md text-sm">
            {submitting ? <span className="spin" /> : "Create Code"}
          </button>
        </div>
      </form>
    </div>
  );
}
